import { Link } from "@/i18n/Link";
import { ArrowRight, Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "./PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, productCategoryJsonLd } from "./Seo";
import { Breadcrumbs } from "./Breadcrumbs";
import { SITE_PHONE_DISPLAY, SITE_URL } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    home: "Home",
    products: "Products",
    orderOnline: "Order Online",
    stock: "What we stock",
    configurations: "Common configurations",
    glance: "At a glance",
    help: "Need help choosing?",
    helpDesc: "Our service team has been spec'ing trailer parts for Elkhart-area customers for decades. We'll get you the right part the first time.",
    contact: "Contact our team",
    related: "Related categories",
    explore: "Explore",
    inEnglish: " (in English)"
  },
  es: {
    home: "Inicio",
    products: "Productos",
    orderOnline: "Ordenar en línea",
    stock: "Lo que tenemos en inventario",
    configurations: "Configuraciones comunes",
    glance: "De un vistazo",
    help: "¿Necesita ayuda para elegir?",
    helpDesc: "Nuestro equipo de servicio ha estado especificando partes de remolque para clientes del área de Elkhart durante décadas. Le conseguiremos la parte correcta a la primera.",
    contact: "Contacte a nuestro equipo",
    related: "Categorías relacionadas",
    explore: "Explorar",
    inEnglish: " (en inglés)"
  }
};

export interface ProductPageProps {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  hero: {
    eyebrow: string;
    heading: string;
    lede: string;
  };
  intro: string;
  features: string[];
  capacityTable?: { label: string; value: string }[];
  variants?: { name: string; desc: string }[];
  related: { title: string; path: string; desc: string }[];
}

export function ProductPageTemplate(p: ProductPageProps) {
  const t = useCopy(COPY);
  const path = `/products/${p.slug}/`;
  const trail = [
    { name: t.home, path: "/" },
    { name: t.products, path: "/products/" },
    { name: p.title, path },
  ];
  const productSchemas = productCategoryJsonLd({
    name: p.title,
    description: p.metaDescription,
    path,
    variants: p.variants,
  });
  return (
    <PageLayout>
      <Seo
        title={p.metaTitle}
        description={p.metaDescription}
        path={path}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          ...(Array.isArray(productSchemas) ? productSchemas : [productSchemas]),
        ]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-white font-semibold mb-4">
            {p.hero.eyebrow}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {p.hero.heading}
          </h1>
          <p className="mt-5 text-base md:text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {p.hero.lede}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">
                {t.orderOnline}{t.inEnglish} <ArrowRight className="ml-1.5 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>
      <section className="container mx-auto px-4 py-16 md:py-20 grid gap-12 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <p className="text-lg leading-relaxed text-foreground/85">{p.intro}</p>
          <div>
            <h2 className="text-xl md:text-2xl font-semibold mb-4">{t.stock}</h2>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex gap-2.5 text-sm leading-relaxed">
                  <Check className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          {p.variants && p.variants.length > 0 && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold mb-4">{t.configurations}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {p.variants.map((v) => (
                  <div key={v.name} className="border border-border rounded-md p-4 bg-card">
                    <div className="font-semibold text-sm mb-1">{v.name}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="md:col-span-1">
          <div className="border border-border rounded-md p-6 bg-card">
            {p.capacityTable && p.capacityTable.length > 0 && (
              <>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t.glance}
                </h3>
                <dl className="divide-y divide-border text-sm">
                  {p.capacityTable.map((row) => (
                    <div key={row.label} className="flex justify-between py-2.5 gap-4">
                      <dt className="text-muted-foreground">{row.label}</dt>
                      <dd className="font-medium text-right">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <hr className="my-5 border-border" />
              </>
            )}
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t.help}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              {t.helpDesc}
            </p>
            <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/contact/">{t.contact}</Link>
            </Button>
          </div>
        </aside>
      </section>
      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-14">
          <h2 className="text-xl md:text-2xl font-semibold mb-8">{t.related}</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {p.related.map((r) => (
              <Link
                key={r.path}
                to={r.path}
                className="group block rounded-md border border-border bg-card p-5 hover:border-primary hover:shadow-sm transition-all"
              >
                <div className="font-semibold mb-1 group-hover:text-primary transition-colors">
                  {r.title}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-primary-strong">
                  {t.explore} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <link rel="canonical" href={`${SITE_URL}${path}`} />
    </PageLayout>
  );
}
